import HttpRequest from "@/util/HttpRequest";

const promiseCache = new Map();

function getCacheKey(actionName, params) {
  return `${actionName}:${JSON.stringify(params)}`;
}

/**
 * API 호출 결과를 캐싱하는 함수
 * @param {string} cacheKey - 캐시 키
 * @param {Function} apiCallFn - API 호출 함수
 * @param {number} memCacheTime - 메모리 캐싱 시간 (밀리초), 기본값 10분
 * @param {boolean} useLocalStorage - 로컬 스토리지 사용 여부, 기본값 false
 * @param {number} localStorageTime - 로컬 스토리지 캐싱 시간 (밀리초), 기본값 5분
 * @returns {Promise} API 호출 결과를 담은 Promise
 */
function cachedApiCall(cacheKey, apiCallFn, memCacheTime = 600000, useLocalStorage = false, localStorageTime = 300000) {
  // 1. 먼저 메모리 캐시 확인
  if (promiseCache.has(cacheKey)) {
    return promiseCache.get(cacheKey);
  }

  // 2. 로컬 스토리지 캐시 확인 (사용하는 경우)
  if (useLocalStorage) {
    const lsKey = `ls_cache_${cacheKey}`;
    const cached = localStorage.getItem(lsKey);

    if (cached) {
      try {
        const { data, expiry } = JSON.parse(cached);
        // 만료 시간 확인
        if (expiry > Date.now()) {
          // 유효한 캐시가 있으면 Promise로 래핑하여 반환
          const cachedPromise = Promise.resolve(data);
          promiseCache.set(cacheKey, cachedPromise);
          return cachedPromise;
        } else {
          // 만료된 캐시는 삭제
          localStorage.removeItem(lsKey);
        }
      } catch (e) {
        // JSON 파싱 오류 시 캐시 삭제
        localStorage.removeItem(lsKey);
      }
    }
  }

  // 3. 캐시 없음, API 호출 실행
  const promise = apiCallFn().then(response => {
    // 로컬 스토리지에 저장 (사용하는 경우)
    if (useLocalStorage) {
      try {
        const lsKey = `ls_cache_${cacheKey}`;
        const cacheData = {
          data: response,
          expiry: Date.now() + localStorageTime
        };
        localStorage.setItem(lsKey, JSON.stringify(cacheData));
      } catch (e) {
        // 저장 실패 시 무시 (로컬 스토리지 크기 초과 등의 이유)
        console.warn('Failed to cache in localStorage:', e);
      }
    }
    return response;
  }).finally(() => {
    // 메모리 캐시 만료 처리
    setTimeout(() => {
      promiseCache.delete(cacheKey);
    }, memCacheTime);
  });

  // 메모리 캐시에 저장 후 반환
  promiseCache.set(cacheKey, promise);
  return promise;
}

export default {
  /**
   * 회사 목록
   * @params {page, perPage}
   */
  async LOAD_COMPANY_LIST({ commit }, params) {
    if (!params.projectId) return;

    const cacheKey = getCacheKey('LOAD_COMPANY_LIST', params);

    return cachedApiCall(cacheKey, () => {
      // onlyRelated - dksteel 전용 파라미터
      params.onlyRelated = false;
      const option = { params };

      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/company", option)
          .then((response) => {
            // mutations 메소드 호출
            commit("LOAD_COMPANY_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },

  /**
   * 회사 목록
   * @params {page, perPage}
   */
  async LOAD_COMPANY_TREE({ commit }, params) {
    if (!params.projectId) return;

    const cacheKey = getCacheKey('LOAD_COMPANY_TREE', params);

    return cachedApiCall(cacheKey, () => {
      const option = {
        params: {
          projectId: params.projectId,
        },
      };

      const subCompanies = localStorage.getItem("sub_company");
      if (subCompanies && !params.new) {
        try {
          const companies = JSON.parse(subCompanies);
          commit("LOAD_COMPANY_TREE", companies);
          return Promise.resolve(companies);
        } catch (e) {
          // localStorage에 저장된 데이터가 잘못되었으면 API 호출
        }
      }

      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/company/tree", option)
          .then((response) => {
            localStorage.setItem("sub_company", JSON.stringify(response));

            // mutations 메소드 호출
            commit("LOAD_COMPANY_TREE", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },

  async LOAD_COMPANY({ commit, state }, params) {
    const companyId = params.companyId;
    const force = params.force;

    if (force || !state.companyInfo || `${state.companyInfo.id}` !== `${companyId}`) {
      const cacheKey = getCacheKey('LOAD_COMPANY', params);

      return cachedApiCall(cacheKey, () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/company/${companyId}`)
            .then((response) => {
              // mutations 메소드 호출
              commit("LOAD_COMPANY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    } else {
      return state.companyInfo;
    }
  },

  async LOAD_VIRTUAL_COMPANY_LIST({ commit }, params) {
    const cacheKey = getCacheKey('LOAD_VIRTUAL_COMPANY_LIST', params);

    return cachedApiCall(cacheKey, () => {
      const option = { params };

      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/company/v", option)
          .then((response) => {
            // mutations 메소드 호출
            commit("LOAD_VIRTUAL_COMPANY_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },

  /**
   * 사용자가 속한 가상 업체 정보
   * {
   *     "data": {
   *         "id": 8,
   *         "company_group_id": 1,
   *         "code": "DKS50143379",
   *         "com_name": "안전환경기획팀",
   *         "parent_code": "DKS50106483",
   *         "full_code": [
   *             "DKG",
   *             "DKS",
   *             "DKSM",
   *             "DKS50106483",
   *             "DKS50143379"
   *         ],
   *         "full_name": [
   *             "동국제강그룹",
   *             "동국제강",
   *             "관리직",
   *             "기획실",
   *             "안전환경기획팀"
   *         ],
   *         "permission": null,
   *         "meta": null,
   *         "company_group": {
   *             "id": 1,
   *             "group_name": "동국제강",
   *             "tree": [ ... ]
   *         }
   *     },
   *     "cache_meta": {
   *         "from_cache": false,
   *         "ttl_remaining": 600
   *     }
   * }
   *
   * @param commit
   * @param state
   * @returns {Promise<unknown>}
   * @constructor
   */
  async LOAD_MY_VIRTUAL_COMPANY({ commit, state }) {
    const isEmpty = Object.keys(state.virtualCompanyInfo).length === 0 && state.virtualCompanyInfo.constructor === Object;
    if (isEmpty) {
      const cacheKey = getCacheKey('LOAD_MY_VIRTUAL_COMPANY', {});

      return cachedApiCall(cacheKey, () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/company/v/my`)
            .then((response) => {
              // mutations 메소드 호출
              commit("LOAD_MY_VIRTUAL_COMPANY", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      });
    } else {
      return state.virtualCompanyInfo;
    }
  },

  async LOAD_COMPANY_FIELD({ commit }, params) {
    const cacheKey = getCacheKey('LOAD_COMPANY_FIELD', params);

    return cachedApiCall(cacheKey, () => {
      const companyId = params.companyId;

      const option = {
        params: {
          page: params.page,
          perPage: params.perPage,
          fieldName: params.fieldName,
          projectId: params.projectId,
          fieldType: params.fieldType,
          fieldCode: params.fieldCode,
        },
      };

      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/company/field/${companyId}`, option)
          .then((response) => {
            // mutations 메소드 호출
            commit("LOAD_COMPANY_FIELD", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },

  async LOAD_MEMBER_LIST({ commit }, params) {
    const cacheKey = getCacheKey('LOAD_MEMBER_LIST', params);

    return cachedApiCall(cacheKey, () => {
      const option = {
        params: {
          page: params.page,
          perPage: params.perPage,
          company_id: params.company_id,
        },
      };

      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/company/member", option)
          .then((response) => {
            // mutations 메소드 호출
            commit("LOAD_MEMBER_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    });
  },
};
